// ═════════════════════════════════════════════════════════════════════════════
// Mack · REFERENCE tab (build-08) — THE MANUAL
//
// SOURCE: scratchpad/mack_design_refs/build-08_tab-09_reference.png
// Colours INHERIT build-01 tokens + C.teal. Nothing new.
//
// THE ONE GENUINELY STATIC TAB. Nothing here computes or carries live state —
// it is method, definitions and lookup tables, and it exists so the working
// surfaces stay uncluttered. It is what you reach for mid-read, not something
// you pass through. It sits ninth in the strip; Overview is first and is built
// after this one.
//
// ⚠ EVERY ACCORDION ENTRY IN THE DESIGN IS COLLAPSED. Not one body is visible in
// the capture, so not one body is transcribed here. Canon is the thing that must
// never be paraphrased — a paraphrased rule is a wrong rule — so each entry
// declares its body UNREAD rather than inventing prose that looks authoritative.
// Named explicitly in the report; the store supplies the bodies.
//
// THE EXCEPTION IS SHARED TABLES. Three entry bodies are known to carry tables
// that already exist as live data on other tabs — the participant roster, block
// sensitivity, and rotation propensity. Those render from the ORIGINAL data
// rather than a third copy, same principle that proved the three-tab chain in
// build-06. Reference reads them; it does not own them.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';
import { C, MONO } from './tokens';
import { card, panelTitle, panelNote, outlinePill, microLabel, Chip } from './primitives';
import { MACK_PHASE_SHEETS, MACK_M_WEIGHTS } from './PhasesTab';
import { MACK_CALC_TIERS } from './CalculatorTab';
import { MACK_ROTATION } from './FlowTab';
import { classifyStage } from './stages';
import { BLOCK_ORDER } from './blocks';

// ── TYPES ────────────────────────────────────────────────────────────────────

export interface MackStageCard {
  icon: string;
  name: string;
  signature: string;   // growth/inflation signature and market character, verbatim
  sectors: string[];   // the leading sectors, rendered inline and teal
  // NO is_live FIELD — derived from the sheets, per the standing ruling, and the
  // design confirms the derivation: it marks Stagflation, Expansion and Reflation
  // live and leaves Deflation unmarked, which is exactly what the Phases sheets say.
}

// 'shared-table' bodies render live data. 'unread' bodies are not in the capture.
export type MackEntryBody =
  | { kind: 'unread' }
  | { kind: 'shared-table'; table: 'roster' | 'sensitivity' | 'rotation' };

export interface MackReferenceEntry {
  n: string;
  title: string;
  subtitle: string;
  category: string;
  body: MackEntryBody;
}

// ── FILTERS ──────────────────────────────────────────────────────────────────
// Exactly the six chips the design shows.
//
// The ORDERING chip is an OWNER RULING (build-09), not the design: entry 01
// carries the category tag ORDERING and the design ships no chip for it, which
// left that entry unreachable under any specific filter. Build-08 transcribed
// the omission and flagged it; this is the ruled repair. Every other chip is
// verbatim from the design.
const FILTERS = [
  { key: 'all',         label: 'All' },
  { key: 'ordering',    label: 'Ordering' },
  { key: 'structure',   label: 'Structure' },
  { key: 'players',     label: 'Players' },
  { key: 'method',      label: 'Method' },
  { key: 'seasonality', label: 'Seasonality' },
  { key: 'config',      label: 'Config' },
];

// ── STAGE CARDS ──────────────────────────────────────────────────────────────
// Transcribed verbatim. The icons are the design's glyphs.
export const MACK_STAGE_CARDS: MackStageCard[] = [
  { icon: '▼', name: 'Deflation',
    signature: 'Slowing growth, falling inflation. Bonds rally, USD weakens, gold up.',
    sectors: ['Staples', 'Utilities', 'Healthcare', 'Gold'] },
  { icon: '⚠', name: 'Stagflation',
    signature: 'Slowing growth, rising inflation. Bonds sell off, USD strengthens.',
    sectors: ['Energy', 'Gold', 'Staples'] },
  { icon: '▲', name: 'Expansion',
    signature: 'Rising growth, stable inflation. Risk-on, USD softer.',
    sectors: ['Tech', 'Financials', 'Industrials'] },
  { icon: '↗', name: 'Reflation',
    signature: 'Rising growth, rising inflation. Cyclicals lead, oil & copper up.',
    sectors: ['Energy', 'Materials', 'Financials'] },
];

// ── ENTRIES ──────────────────────────────────────────────────────────────────
// All fifteen headers are transcribed verbatim — number, title, subtitle and
// category tag. Every BODY is marked unread except the three shared tables.
export const MACK_REFERENCE_ENTRIES: MackReferenceEntry[] = [
  { n: '01', title: 'The route',                          subtitle: 'Who is it → what symbol → why are they reacting',      category: 'ORDERING',    body: { kind: 'unread' } },
  { n: '02', title: 'Country sheets',                     subtitle: 'Five sheets plus one bloc — absolute, not paired',     category: 'STRUCTURE',   body: { kind: 'unread' } },
  { n: '03', title: 'Reactive against sleeping',          subtitle: 'The separating layer',                                 category: 'STRUCTURE',   body: { kind: 'unread' } },
  { n: '04', title: 'The participant roster',             subtitle: 'Country-resident, not global categories',              category: 'PLAYERS',     body: { kind: 'shared-table', table: 'roster' } },
  { n: '05', title: 'Block sensitivity — the routing table', subtitle: 'What turns the floor vector into an attribution',   category: 'PLAYERS',     body: { kind: 'shared-table', table: 'sensitivity' } },
  { n: '06', title: 'Structural invisibility',            subtitle: 'Why pensions barely appear in the tape',               category: 'PLAYERS',     body: { kind: 'unread' } },
  { n: '07', title: 'Capacity — R, the eliminator',       subtitle: 'Where the law applies and where it stops',             category: 'METHOD',      body: { kind: 'unread' } },
  { n: '08', title: 'Activation',                         subtitle: 'Weight is size × activation, not size',                category: 'METHOD',      body: { kind: 'unread' } },
  { n: '09', title: 'Seasonality',                        subtitle: 'Named participant, named mechanism, or no weight',     category: 'SEASONALITY', body: { kind: 'shared-table', table: 'rotation' } },
  { n: '10', title: 'Trust rules',                        subtitle: 'Stamped at write time from provenance',                category: 'METHOD',      body: { kind: 'unread' } },
  { n: '11', title: 'The config file',                    subtitle: 'Every uncalibrated quantity, one hash, in every record', category: 'CONFIG',    body: { kind: 'unread' } },
  { n: '12', title: 'The record',                         subtitle: 'Full input state, logged backend, never on screen',    category: 'CONFIG',      body: { kind: 'unread' } },
  { n: '13', title: 'The three timescales',               subtitle: 'Cycle changes the counting, never the maths',          category: 'STRUCTURE',   body: { kind: 'unread' } },
  { n: '14', title: 'Backtesting',                        subtitle: 'Same chain, past date, blinded commit',                category: 'METHOD',      body: { kind: 'unread' } },
  { n: '15', title: 'Conflict-resolution hierarchy',      subtitle: 'Which currency cycle takes precedence',                category: 'STRUCTURE',   body: { kind: 'unread' } },
];

// ── DERIVED ──────────────────────────────────────────────────────────────────
// Which stages are live right now, straight off the sheets. The design marks
// three; the Phases sheets classify to exactly those three.
function liveStages(): Set<string> {
  return new Set(MACK_PHASE_SHEETS.map(s => classifyStage(s.delta_growth, s.delta_inflation)));
}

// The six blocks, from the one module that declares them (build-10).
function blockKeys(): string[] { return BLOCK_ORDER as unknown as string[]; }

const SHEET_COLS = ['USD', 'GBP', 'EUR', 'JPY', 'AUD', 'BLOC'] as const;
const SHEET_LABEL: Record<string, string> = {
  USD: 'US', GBP: 'UK', EUR: 'EUR', JPY: 'JPY', AUD: 'AUD', BLOC: 'BLOC',
};

const monoSmall: React.CSSProperties = { fontSize: 11.5, fontFamily: MONO };

// Measured (content 1206px → ×1.227 → 1480 CSS): number 32 · body flex · tag 112 · caret 18.
const ENTRY_GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '32px minmax(0,1fr) 118px 18px',
  alignItems: 'center', gap: 10,
};

// ── SHARED-TABLE BODIES ──────────────────────────────────────────────────────
// These read the ORIGINAL data from the tabs that own it. Reference does not
// carry its own copy of any of them.

function SharedTableNote({ source }: { source: string }) {
  return (
    <div style={{ ...monoSmall, color: C.textMuted, marginTop: 12, fontStyle: 'italic' }}>
      Rendered from {source} — Reference reads this table, it does not hold a copy.
    </div>
  );
}

function RosterTable() {
  const grid: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: 'minmax(0,1fr) repeat(6, 74px) 96px',
    alignItems: 'center',
  };
  const R = (v: React.ReactNode, k?: string) =>
    <span key={k} style={{ ...monoSmall, color: C.textSub, textAlign: 'right' }}>{v}</span>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 660 }}>
        <div style={{ ...grid, padding: '0 0 9px', borderBottom: `1px solid ${C.rule}` }}>
          <span style={microLabel}>Tier</span>
          {SHEET_COLS.map(c => <span key={c} style={{ ...microLabel, textAlign: 'right' }}>{SHEET_LABEL[c]}</span>)}
          <span style={{ ...microLabel, paddingLeft: 14 }}>Speed</span>
        </div>
        {MACK_M_WEIGHTS.map(r => (
          <div key={r.tier} style={{ ...grid, minHeight: 36, borderBottom: `1px solid ${C.rule}` }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{r.tier}</span>
            {SHEET_COLS.map(c => R(r.M[c].toFixed(2), `${r.tier}-${c}`))}
            <span style={{ ...monoSmall, color: C.textMuted, paddingLeft: 14, textTransform: 'uppercase' }}>{r.speed}</span>
          </div>
        ))}
      </div>
      <SharedTableNote source="the Phases M matrix" />
    </div>
  );
}

function SensitivityTable() {
  const blocks = blockKeys();
  const grid: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: `minmax(0,1fr) repeat(${blocks.length}, 88px)`,
    alignItems: 'center',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 640 }}>
        <div style={{ ...grid, padding: '0 0 9px', borderBottom: `1px solid ${C.rule}` }}>
          <span style={microLabel}>Tier</span>
          {blocks.map(b => <span key={b} style={{ ...microLabel, textAlign: 'right' }}>{b}</span>)}
        </div>
        {MACK_CALC_TIERS.map(t => (
          <div key={t.name} style={{ ...grid, minHeight: 36, borderBottom: `1px solid ${C.rule}` }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{t.name}</span>
            {blocks.map(b => {
              const v = (t.sensitivity as Record<string, number>)[b];
              return (
                <span key={`${t.name}-${b}`} style={{
                  ...monoSmall, textAlign: 'right',
                  color: v === 0 ? C.textMuted : v >= 3 ? C.green : v >= 2 ? C.amber : C.textSub,
                }}>{v}</span>
              );
            })}
          </div>
        ))}
      </div>
      <SharedTableNote source="the Calculator sensitivity matrix" />
    </div>
  );
}

function RotationTable() {
  const grid: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '220px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',
    alignItems: 'center',
  };
  const live = liveStages();
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 620 }}>
        <div style={{ ...grid, padding: '0 0 9px', borderBottom: `1px solid ${C.rule}` }}>
          <span style={microLabel}>Stage</span>
          <span style={microLabel}>Most volume</span>
          <span style={microLabel}>Second</span>
          <span style={microLabel}>Least</span>
        </div>
        {MACK_ROTATION.map(r => (
          <div key={r.stage} style={{ ...grid, minHeight: 36, borderBottom: `1px solid ${C.rule}` }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: live.has(r.stage) ? C.green : C.text }}>
              {r.stage}
            </span>
            <span style={{ fontSize: 12.5, color: C.amber }}>{r.most}</span>
            <span style={{ fontSize: 12.5, color: C.textSub }}>{r.second}</span>
            <span style={{ fontSize: 12.5, color: C.textSub }}>{r.least}</span>
          </div>
        ))}
      </div>
      <SharedTableNote source="the Flow rotation table" />
    </div>
  );
}

// The honest body for the twelve entries the capture never opens.
function UnreadBody({ title }: { title: string }) {
  return (
    <div style={{
      background: C.formulaBg, borderLeft: `3px solid ${C.textMuted}`,
      borderRadius: '3px 8px 8px 3px', padding: '13px 16px',
      fontSize: 12.5, lineHeight: 1.65, color: C.textMuted,
    }}>
      <span style={{ color: C.textSub, fontWeight: 700 }}>Body not transcribed.</span>{' '}
      Every entry in the design capture ships collapsed, so the body of “{title}” was never visible.
      This is canon, and a paraphrased rule is a wrong rule — so nothing is invented here. Awaiting
      either a capture with this entry expanded or the store.
    </div>
  );
}

// ── TAB ──────────────────────────────────────────────────────────────────────
//
// INTERACTIONS: the filter chips and the accordion, and nothing else. The design
// shows no other affordance. Whether more than one entry may sit open at once
// cannot be read from a capture in which all fifteen are closed — multiple open
// is the choice here, flagged as unconfirmed.

export function ReferenceTab() {
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  const live = useMemo(() => liveStages(), []);

  const entries = useMemo(
    () => filter === 'all'
      ? MACK_REFERENCE_ENTRIES
      : MACK_REFERENCE_ENTRIES.filter(e => e.category.toLowerCase() === filter),
    [filter],
  );

  const toggle = (n: string) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>

      {/* ── HEADER ───────────────────────────────────────────────────── */}
      <div style={{ ...card, padding: '17px 22px 19px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={panelTitle}>The manual</div>
            <div style={{ ...panelNote, maxWidth: 1000, marginTop: 6 }}>
              Method, definitions and lookup tables. Nothing here computes or carries live state.
            </div>
          </div>
          <span style={{ ...outlinePill(C.violet), flexShrink: 0 }}>Tier 1 · Facts</span>
        </div>
      </div>

      {/* ── FILTER CHIPS — on the page ground, not inside a panel ────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <Chip key={f.key} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
        ))}
      </div>

      {/* ── STAGE CARDS ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 17 }}>
        {MACK_STAGE_CARDS.map(s => {
          const isLive = live.has(s.name);
          return (
            <div key={s.name} style={{
              ...card, padding: '15px 17px 17px',
              // The live cards carry a green edge in the design; the dormant one
              // keeps the standard warm border.
              border: `1px solid ${isLive ? C.green : C.cardBorder}`,
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: 7,
                border: `1px solid ${C.amberDim}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: C.amber, fontSize: 12,
              }}>{s.icon}</div>

              <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginTop: 13 }}>{s.name}</div>

              <div style={{ fontSize: 12.5, color: C.textSub, lineHeight: 1.6, marginTop: 7, flex: 1 }}>
                {s.signature}{' '}
                {s.sectors.map((sec, i) => (
                  <React.Fragment key={sec}>
                    <span style={{ color: C.teal }}>{sec}</span>
                    {i < s.sectors.length - 1 && <span style={{ color: C.textMuted }}> · </span>}
                  </React.Fragment>
                ))}
              </div>

              {/* The footer text itself is conditional in the design, not just its colour. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 13 }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: isLive ? C.green : C.textMuted, flexShrink: 0,
                }} />
                <span style={{ ...monoSmall, fontSize: 10.5, color: C.textMuted }}>
                  {isLive ? 'live on a sheet now' : 'framework · FACTS tier'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── ACCORDION ENTRIES ───────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entries.length === 0 && (
          <div style={{ ...card, padding: '22px', textAlign: 'center', color: C.textMuted, fontSize: 12.5 }}>
            No entries in this category.
          </div>
        )}
        {entries.map(e => {
          const isOpen = open.has(e.n);
          return (
            <div key={e.n} style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <button
                onClick={() => toggle(e.n)}
                aria-expanded={isOpen}
                style={{
                  ...ENTRY_GRID, width: '100%', minHeight: 66,
                  padding: '12px 20px', background: 'none', border: 'none',
                  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 10.5, fontFamily: MONO, color: C.amberDim }}>{e.n}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: C.text }}>{e.title}</span>
                  <span style={{ display: 'block', fontSize: 12, color: C.textMuted, marginTop: 3 }}>{e.subtitle}</span>
                </span>
                <span style={{
                  fontSize: 9, fontFamily: MONO, color: C.textMuted,
                  border: `1px solid ${C.chipOffBd}`, borderRadius: 4,
                  padding: '3px 8px', letterSpacing: '0.12em', textAlign: 'center',
                }}>{e.category}</span>
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"
                     style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
                     fill="none" stroke={C.textMuted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 3.5 10.5 8 6 12.5" />
                </svg>
              </button>

              {isOpen && (
                <div style={{ padding: '4px 20px 20px', borderTop: `1px solid ${C.rule}`, marginTop: -1 }}>
                  <div style={{ paddingTop: 16 }}>
                    {e.body.kind === 'unread' && <UnreadBody title={e.title} />}
                    {e.body.kind === 'shared-table' && e.body.table === 'roster' && <RosterTable />}
                    {e.body.kind === 'shared-table' && e.body.table === 'sensitivity' && <SensitivityTable />}
                    {e.body.kind === 'shared-table' && e.body.table === 'rotation' && <RotationTable />}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
